import React, { useCallback, useContext, useEffect, useState } from 'react';
import { CALCULATE_TEST_DURATION, RECALCULATE_TEST_DURATION, SEASON_DURATION, TEST_DESIGN, TEST_DESIGN_INFO } from '../../utils/Constants';
import CustomRadioButton from '../../components/CustomRadioButton';
import { scheduleItems } from '../../utils/ABHelper';
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import CustomInput from '../../components/CustomInput';
import { ABContext } from '../../contexts/ABContext';
import { calculateABTestDuration } from '../../services/ABServices';
import { CalculateTestDurationPayload, TPayload } from '../../types/ab';
import { toast } from "react-toastify";
import { areObjectsIdentical } from 'Pages/GMS/ISR/Components/RuleComponents/helper';

type TestDesignSectionProps = { testId: string, onSave: () => Promise<boolean | undefined>; }

const TestDesignSection = ({ testId, onSave }: TestDesignSectionProps) => {
    const abContext = useContext(ABContext);
    const [loading, setLoading] = useState(false);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    useEffect(() => {
        if (abContext?.payload) {
            abContext.setPayload((prevPayload: TPayload) => ({
                ...prevPayload,
                metric: prevPayload.metric || 'Revenue',
                is_seasonal: prevPayload.shopping_cycle === 'Seasonal' ? true : (prevPayload.is_seasonal ?? false),
                season_end: prevPayload.season_end,
                season_start: prevPayload.season_start,
                shopping_cycle: prevPayload.shopping_cycle
            }));
        }
    }, []);

    const handleShoppingCycleChange = useCallback((selectedShoppingCycle: string | ((prevState: string) => string)) => {
        if (abContext?.setPayload) {
            abContext.setPayload((prevPayload: TPayload) => {
                const updatedShoppingCycle = typeof selectedShoppingCycle === 'function' ? selectedShoppingCycle(prevPayload.shopping_cycle || '') : selectedShoppingCycle;

                const updatedPayload = {
                    ...prevPayload, 
                    //shopping_cycle: updatedShoppingCycle === 'Seasonal' ? 'Daily' : updatedShoppingCycle,
                    shopping_cycle: updatedShoppingCycle,
                    is_seasonal: updatedShoppingCycle === 'Seasonal'
                };
                return updatedPayload;
            });
        }
    }, []);

    // const handleIsSeasonalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     event.persist();
    //     if (abContext?.payload) {
    //         abContext.setPayload((prevPayload: TPayload) => ({
    //             ...prevPayload,
    //             is_seasonal: event.target.checked
    //         }));
    //     }
    // };

    const handleSeasonStartDateChange = (date: Date | null) => {
        if (!date) { return; }
        if (abContext?.payload) {
            abContext.setPayload((prevPayload: TPayload) => ({
                ...prevPayload,
                season_start: date.toISOString()
            }));
        }
    }
    const handleSeasonEndDateChange = (date: Date | null) => {
        if (!date) { return; }
        if (abContext?.payload) {
            abContext.setPayload((prevPayload: TPayload) => ({
                ...prevPayload,
                season_end: date.toISOString()
            }));
        }
    }

    function formatDateForAPI(dateString: string): string {
        const date = new Date(dateString);

        return date.toISOString().replace('Z', '+00:00');
    }

    const scrollToSaveButton = () => {
        const section = document.querySelector('#save-button');
        section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const calculateTestDuration = async () => {
        try {
            setLoading(true); 
            let updatedPayload = abContext?.payload;
            if (!abContext?.payload?.shopping_cycle) { toast.error("Please select shopping cycle"); return; }
            const isSeasonal = abContext?.payload?.shopping_cycle === 'Seasonal';
            const metric = abContext?.payload?.metric == null ? 'Revenue' : abContext?.payload?.metric;
            
            if (!isSeasonal) {
                updatedPayload = {
                    ...updatedPayload,
                    metric: metric,
                    is_seasonal: false,
                    season_end: null,
                    season_start: null,
                };
                abContext.setPayload(updatedPayload);
            }

            if (isSeasonal && (abContext?.payload?.season_start == null || abContext?.payload?.season_end == null)) {
                toast.error("Please select season start and end date")
                return;
            }

            if (abContext?.payload?.applets == null || updatedPayload?.metric == null) {
                toast.error("Please save the test details to calculate the test duration")
                scrollToSaveButton();
                return;
            }

            const formattedStartDate = formatDateForAPI(updatedPayload?.season_start)
            const formattedEndDate = formatDateForAPI(updatedPayload?.season_end)

            
            let shoppingCycleForAPI = abContext?.payload?.shopping_cycle;
            
            if (shoppingCycleForAPI === 'Seasonal') {
                
            
                shoppingCycleForAPI = 'Daily';
            }
            
            const requestBody: CalculateTestDurationPayload = {
                shopping_cycle: shoppingCycleForAPI,
                is_seasonal: isSeasonal,
                ...(isSeasonal && {
                    season_start: formattedStartDate,
                    season_end: formattedEndDate
                })
            };
            
            if (!areObjectsIdentical(abContext?.payload, abContext?.payloadNetworkCopy)) {
                const saved = await onSave();
                if (!saved) return;
            }
            const response = await calculateABTestDuration(testId, requestBody);
            if (response?.done) {
                if (abContext?.payloadNetworkCopy) {
                    abContext?.setPayloadNetworkCopy((prevPayload: TPayload) => ({
                        ...prevPayload,
                        estimated_orders_per_day: response?.data?.estimated_orders_per_day,
                        estimated_sessions_per_day: response?.data?.estimated_sessions_per_day,
                        test_duration: response?.data?.test_duration,
                    }));
                }
                if (abContext?.payload) {
                    abContext?.setPayload((prevPayload: TPayload) => ({
                        ...prevPayload,
                        estimated_orders_per_day: response?.data?.estimated_orders_per_day,
                        estimated_sessions_per_day: response?.data?.estimated_sessions_per_day,
                        test_duration: response?.data?.test_duration,
                    }));
                }
                toast.success(response?.message ?? "Test duration calculated successfully!")
            } else {
                toast.error(response?.message)
            }
        } catch (error) {
            console.log("getModels ~ error:", error);
        } finally {
            setLoading(false);
        }
    }
    

    const seasonStartDate = abContext?.payload?.season_start ? new Date(abContext.payload.season_start) : null;
    const seasonEndDate = abContext?.payload?.season_end ? new Date(abContext.payload.season_end) : null;


    const showSeasonalDates = abContext?.payload?.shopping_cycle === 'Seasonal';

    return (
        <section className='tw-border-b tw-pb-6'>
            <label className='tw-font-semibold tw-text-black tw-text-sm'>{TEST_DESIGN}</label>
            <div className='tw-mt-4'>
                <label className="text-caption-style tw-text-[#747593] tw-mb-2">{TEST_DESIGN_INFO}</label>
                <CustomRadioButton list={scheduleItems} selectedItem={abContext?.payload?.shopping_cycle} setSelectedItem={handleShoppingCycleChange} />
            </div>
           
            {showSeasonalDates && <label className='text-caption-style tw-text-[#747593] tw-mt-4'>{SEASON_DURATION}</label>}
            {showSeasonalDates && <div className="tw-flex tw-space-x-4 tw-mt-2">
                <div className="tw-flex tw-flex-col">
                    <label className="tw-mb-2 tw-text-sm">From</label>
                    <DatePicker
                        selected={seasonStartDate}
                        onChange={handleSeasonStartDateChange}
                        dateFormat="dd/MMM/yyyy"
                        minDate={today}
                        customInput={<CustomInput />}
                    />
                </div>
                <div className="tw-flex tw-flex-col">
                    <label className="tw-mb-2 tw-text-sm">To</label>
                    <DatePicker
                        selected={seasonEndDate}
                        onChange={handleSeasonEndDateChange}
                        minDate={seasonStartDate ? new Date(seasonStartDate.getTime() + 24*60*60*1000) : today} 
                        dateFormat="dd/MMM/yyyy"
                        customInput={<CustomInput />}
                    />
                </div>
            </div>}
            <button className='outline-button tw-mt-4' onClick={calculateTestDuration} disabled={loading}>
                {loading ? (
                    <span className="tw-flex tw-items-center tw-justify-center">
                        <svg className="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Calculating...
                    </span>
                ) : (
                    abContext?.payload?.estimated_orders_per_day ? RECALCULATE_TEST_DURATION : CALCULATE_TEST_DURATION
                )}
            </button>
        </section>
    );
}

export default TestDesignSection;